import type { NumberFormatterOptions } from "../../../../../../../../../../api/types/fieldConfigurationTypes";
import type { CustomColumnField } from "../../../../../../../../../../api/types/svTypes";
import type { FormulaReferenceField } from "./customFormulaParser";
import { parseCustomFormula } from "./customFormulaParser";
import { escapeFormulaQuotedValue } from "./customFormulaQuotedValue";

export interface CustomFormulaDialogInitialValues {
  name: string;
  formulaInput: string;
  format?: NumberFormatterOptions;
  blankIfMissingValue: boolean;
}

const defaultInitialValues: CustomFormulaDialogInitialValues = {
  name: "New Calculation",
  formulaInput: "",
  format: undefined,
  blankIfMissingValue: false,
};

export const hydrateFormulaPlaceholders = (formula: string, formulaParameters: string[]): string => {
  return formula.replace(/\{(\d+)\}/g, (placeholder: string, parameterIndexValue: string) => {
    const parameterIndex = Number(parameterIndexValue);
    if (!Number.isFinite(parameterIndex) || parameterIndex < 0) {
      return placeholder;
    }

    const fieldGuid = formulaParameters[parameterIndex];
    if (fieldGuid === undefined) {
      return placeholder;
    }

    return `"${escapeFormulaQuotedValue(fieldGuid)}"`;
  });
};

const getInitialName = (field: CustomColumnField): string => {
  return field.config.customLabel ?? field.config.name ?? field.meta.caption;
};

const getInitialFormulaInput = (field: CustomColumnField, referenceFields: FormulaReferenceField[]): string => {
  const formula = field.config.formula ?? "";
  if (!formula) {
    return "";
  }

  const formulaWithResolvedPlaceholders = hydrateFormulaPlaceholders(formula, field.config.formulaParameters ?? []);
  const parsedFormulaResult = parseCustomFormula(formulaWithResolvedPlaceholders, referenceFields);
  if (!parsedFormulaResult.isValid || !parsedFormulaResult.parsed) {
    return formulaWithResolvedPlaceholders;
  }

  return parsedFormulaResult.parsed.displayFormula;
};

export const getCustomFormulaDialogInitialValues = (
  field: CustomColumnField | undefined,
  referenceFields: FormulaReferenceField[],
  sourceFieldGuid: string | undefined
): CustomFormulaDialogInitialValues => {
  if (field) {
    return {
      name: getInitialName(field),
      formulaInput: getInitialFormulaInput(field, referenceFields),
      format: field.config.format ?? undefined,
      blankIfMissingValue: field.config.blankIfMissingValue === true,
    };
  }

  if (sourceFieldGuid === undefined) {
    return defaultInitialValues;
  }

  const sourceField = referenceFields.find((item) => item.guid === sourceFieldGuid);
  if (sourceField === undefined) {
    return defaultInitialValues;
  }

  return {
    ...defaultInitialValues,
    formulaInput: `"${escapeFormulaQuotedValue(sourceField.caption)}"`,
  };
};
